/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useRef } from 'react';
import { createContext, useEffect, useMemo, useState, useCallback } from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useRelayEnvironment } from 'react-relay';

import { Box } from '@atlaskit/primitives/compiled/box';
import { LoadingView } from '@atlassian/twg-microsite-common/loading-view';
import { subscribeToSessionRejections } from '@atlassian/twg-microsite-common/session-events';

import { getMicrositeAnonymousId, setMicrositeUser } from '../../common/analytics/analytics-client';
import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { logMessage, setSentryAnonymousUser, setSentryUser } from '../../common/sentry';
import {
	attemptClientLogin,
	cleanupClientAuthFlow,
	consumeLoginPendingMarker,
	hasAutomaticAuthFailed,
} from './auth-flow';
import { type LoggedInUser, type SessionStatus, type UserContextValue } from './types';
import { probeSessionStatus, queryUserProfile, type QueryUserProfileResponse } from './utils';

const styles = cssMap({
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100vw',
		height: '100vh',
	},
});

const EMPTY_IDENTITY: LoggedInUser = {
	id: '',
	picture: '',
	accountId: '',
	name: '',
	nickname: '',
	email: '',
	staffId: '',
	isAtlassianEmployee: false,
	organisation: '',
	jobTitle: '',
	orgId: '',
	createdAt: '',
};

export const UserContext: React.Context<UserContextValue> =
	createContext<UserContextValue>(undefined);

type UserProviderProps = {
	children: React.ReactNode;
	onInitialize?: () => Promise<void>;
	onUserLoginComplete?: (user: LoggedInUser) => Promise<void>;
};

/**
 * Turns the settled profile fetch into a session status.
 *
 * The Relay network layer drops the HTTP status, so when the query threw we
 * cannot tell a refused cookie from a server error without asking the gateway
 * again through a transport that keeps the status.
 */
const classifySession = async (
	profile: PromiseSettledResult<QueryUserProfileResponse>,
): Promise<SessionStatus> => {
	if (profile.status === 'fulfilled') {
		const outcome = profile.value;
		if (outcome.success) {
			return outcome.identity.accountId.length > 0 ? 'authenticated' : 'anonymous';
		}
		if (outcome.reason === 'anonymous') {
			return 'anonymous';
		}
	}

	const probed = await probeSessionStatus();

	// The profile query failed but a direct probe found a session. The UI still
	// has no identity, so report the backend as unavailable rather than claim a
	// login the visitor cannot see.
	if (probed === 'authenticated') {
		return 'unavailable';
	}

	return probed;
};

/**
 * UserProvider manages the authentication state and user profile data.
 *
 * Authentication Flow:
 * 1. On mount, fetches the user profile and waits for feature gate initialisation in parallel.
 * 2. If the profile is incomplete or the fetch fails, `error` is set and handleAuthFailure runs.
 *    When automatic login has already failed too often, the provider exposes `automaticLoginFailed`
 *    so `SessionFlags` can offer a translated retry.
 * 3. The failure is also classified into a `sessionStatus` (see `SessionStatus`), and protected
 *    requests that are refused with a 401
 *    during use report through `@atlassian/twg-microsite-common/session-events`. A report while
 *    authenticated re-checks the session with `probeSessionStatus`; a probe that is refused or finds
 *    no user clears the identity and marks the session `expired`. Reports during the check or after it are ignored, so
 *    concurrent 401s collapse into one transition. Nothing here redirects on its own: recovery is
 *    offered to the user by `SessionFlags`, which calls `attemptUserLogin`.
 */
export function UserProvider({
	children,
	onInitialize,
	onUserLoginComplete,
}: UserProviderProps): React.JSX.Element {
	const [identity, setIdentity] = useState<LoggedInUser>(EMPTY_IDENTITY);
	const loginRedirectFailed = useRef<boolean>(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [sessionStatus, setSessionStatus] = useState<SessionStatus>('loading');
	const [automaticLoginFailed, setAutomaticLoginFailed] = useState(false);
	const [sessionExpiredReturnUrl, setSessionExpiredReturnUrl] = useState<string | null>(null);
	// Mirror of `sessionStatus` for the rejection listener, which must decide
	// synchronously so that two 401s arriving together cannot both transition.
	const sessionStatusRef = useRef<SessionStatus>('loading');
	const environment = useRelayEnvironment();
	const { fireEvent } = useAnalyticsEvents();

	const updateSessionStatus = useCallback((next: SessionStatus) => {
		sessionStatusRef.current = next;
		setSessionStatus(next);
	}, []);

	const handleAuthFailure = useCallback(async () => {
		if (hasAutomaticAuthFailed()) {
			loginRedirectFailed.current = true;
			return;
		}
	}, []);

	const fetchUserProfile = useCallback(async (): Promise<QueryUserProfileResponse> => {
		const res = await queryUserProfile(environment);

		if (res.success) {
			const { identity } = res;

			if (identity.accountId) {
				// Execute post login if provided
				if (onUserLoginComplete) {
					await onUserLoginComplete?.(identity);
				}

				setIdentity(identity);
				setMicrositeUser(identity.accountId, identity.orgId, null);
				setSentryUser(identity.accountId);

				if (consumeLoginPendingMarker()) {
					fireEvent('track.user.loggedIn', {});
				}
			} else {
				setIdentity(identity);
				setSentryAnonymousUser(getMicrositeAnonymousId());
			}
		} else {
			setError(true);
			setSentryAnonymousUser(getMicrositeAnonymousId());
			await handleAuthFailure();
		}

		return res;
	}, [environment, handleAuthFailure, onUserLoginComplete, fireEvent]);

	useEffect(() => {
		// A failure in either task must not release children while the other is pending.
		void Promise.allSettled([fetchUserProfile(), Promise.resolve().then(onInitialize)]).then(
			async ([profile]) => {
				const status = await classifySession(profile);
				updateSessionStatus(status);

				if (status === 'unavailable') {
					logMessage('User profile unavailable', { loggerName: 'userProvider' });
				}

				setLoading(false);
			},
		);
	}, [fetchUserProfile, onInitialize, updateSessionStatus]);

	useEffect(() => {
		if (loading) {
			return;
		}

		let subscribed = true;
		// One confirmation at a time: reports that arrive while the probe is in
		// flight are dropped, since the answer will cover them too.
		let confirming = false;

		const unsubscribe = subscribeToSessionRejections(({ source }) => {
			if (sessionStatusRef.current !== 'authenticated' || confirming) {
				return;
			}
			confirming = true;
			// Where the visitor was when the 401 happened. Signed-out routing may
			// move them once the identity is cleared, so this is captured now.
			const returnUrl = globalThis.window.location.href;

			// A route can answer 401 for reasons of its own while the session is
			// fine, so the report is a prompt to check, not a verdict. The session
			// is gone when the gateway refuses the `me` query, or answers it with no
			// user at all, as after a logout in another tab. A probe that still sees
			// a user, or cannot be made, leaves the visitor signed in.
			void probeSessionStatus()
				.then((probed) => {
					if (!subscribed || sessionStatusRef.current !== 'authenticated') {
						return;
					}

					if (probed === 'authenticated' || probed === 'unavailable') {
						logMessage('Protected request returned 401 but the session is still valid', {
							loggerName: 'userProvider',
							source,
							probed,
						});
						return;
					}

					updateSessionStatus('expired');
					setSessionExpiredReturnUrl(returnUrl);
					setIdentity(EMPTY_IDENTITY);
					setError(true);
					setSentryAnonymousUser(getMicrositeAnonymousId());
					logMessage('Session expired during use', {
						loggerName: 'userProvider',
						source,
						probed,
					});
				})
				.finally(() => {
					confirming = false;
				});
		});

		return () => {
			subscribed = false;
			unsubscribe();
		};
	}, [loading, updateSessionStatus]);

	useEffect(() => {
		// `loginRedirectFailed` is written while the profile loads, so this waits
		// for `loading` to settle rather than reading the ref on mount.
		if (loading || !loginRedirectFailed.current) {
			return;
		}

		// Attempting to redirect to login has failed repeatedly. Clear the redirect
		// bookkeeping so the next attempt starts fresh, then offer a way out.
		cleanupClientAuthFlow();

		// `SessionFlags`, rendered below `LocaleProvider`, shows a translated
		// flag with a retry action.
		setAutomaticLoginFailed(true);
	}, [loading, loginRedirectFailed]);

	// `isAuthed` is the single boolean source of truth for "the user is
	// signed in and we know who they are". Pre-computed here so consumers
	// (analytics hooks, route gating in `home/index.tsx`, etc.) don't have
	// to re-derive it on every render.
	const isAuthed = !loading && !error && identity.accountId.length > 0;

	const value = useMemo(
		() => ({
			identity,
			loading,
			error,
			sessionStatus,
			automaticLoginFailed,
			sessionExpiredReturnUrl,
			isAuthed,
			attemptUserLogin: attemptClientLogin,
			handleAuthFailure,
		}),
		[
			identity,
			loading,
			error,
			sessionStatus,
			automaticLoginFailed,
			sessionExpiredReturnUrl,
			isAuthed,
			handleAuthFailure,
		],
	);

	return (
		<UserContext.Provider value={value}>
			{loading ? (
				// Wait for anonymous flags and the profile in parallel. The profile
				// task also awaits logged-in identification before releasing children.
				<Box xcss={styles.loadingContainer} testId="twg-microsite.user-provider.loading">
					<LoadingView appearance="spinner" holdName="twg-microsite.user-provider.loading" />
				</Box>
			) : (
				children
			)}
		</UserContext.Provider>
	);
}
